import { DefaultPalette } from "@fluentui/react";
import { Button, MenuItem, MenuList } from "@fluentui/react-components";
import React, { useState, useCallback, ReactElement } from "react";
import { useNavigate } from "react-router";

import { useAppRouteParams } from "../../../../AppRoutes";
import { View } from "../../../../domain/View";
import { DialogPhaseType } from "../../helpers/hooks";

import {
  BulkActionRequest,
  createIdleRequest,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { DialogViewDelete } from "./DialogViewDelete";
import { DialogViewDeleteSome } from "./DialogViewDeleteSome";
import { useViewDuplicateAction } from "./DialogViewDuplicate";
import { useSelectedViewsDuplicateAction } from "./DialogViewDuplicateSome";

import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { translation } from "@/i18n";
import { nsView } from "@/i18n/locales/view";
import { useWarningButtonStyles } from "@/styles";

const useTranslation = translation.use(nsView);

/**
 * @description 複数の場所で利用されるcontextItemsとダイアログを生成
 * views: 複製・削除処理の対象となるビュー一覧。呼び出し元コンポーネントで取得して渡す。
 */
export const useDialogContextMenuItemsView = (views: View[] = []) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, versionId } = useAppRouteParams();
  const { hasPermission } = usePermissions();
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const [selectedViewIds, setSelectedViewIds] = useState([] as string[]);
  const [deleteDialogPhaseType, setDeleteDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [duplicateRequest, setDuplicateRequest] =
    useState<BulkActionRequest>(createIdleRequest);
  const [isDeleteSomeDialogOpen, setIsDeleteSomeDialogOpen] = useState(false);
  const buttonClasses = useWarningButtonStyles();

  // 複製アクションフック（コンポーネントではなくフックとして直接呼び出す）
  useViewDuplicateAction({
    views,
    duplicateRequest,
    setDuplicateRequest,
    setSelectedViewIds,
  });
  useSelectedViewsDuplicateAction({
    views,
    duplicateRequest,
    setDuplicateRequest,
    setSelectedViewIds,
  });

  const getContextMenuItemsView = useCallback(
    (view: View): ReactElement => {
      return (
        <MenuList>
          <MenuItem
            key="i0"
            onClick={() =>
              navigate(
                `/organizations/${organizationId}/versions/${versionId}/views/${view.id}/edit`
              )
            }
            disabled={!hasVersionsMetaWritePermission}
          >
            {t.view.row.editSetting()}
          </MenuItem>
          <MenuItem
            key="i1"
            onClick={() => {
              setDuplicateRequest((current) =>
                current.phase === "idle"
                  ? { phase: "requested", itemIds: [view.id] }
                  : current
              );
            }}
            disabled={!hasVersionsMetaWritePermission}
          >
            {t.view.row.duplicate()}
          </MenuItem>
          <MenuItem
            key="i2"
            onClick={() => {
              setSelectedViewIds([view.id]);
              setDeleteDialogPhaseType("confirm");
            }}
            disabled={!hasVersionsMetaWritePermission}
            style={{
              color: hasVersionsMetaWritePermission ? DefaultPalette.red : "",
            }}
          >
            {t.view.row.delete()}
          </MenuItem>
        </MenuList>
      );
    },
    [t, hasVersionsMetaWritePermission, organizationId, versionId, navigate]
  );

  const renderDialogsView = useCallback(() => {
    return (
      <>
        <DialogViewDelete
          views={views}
          selectedViewIds={selectedViewIds}
          setSelectedViewIds={setSelectedViewIds}
          dialogPhase={deleteDialogPhaseType}
          setDialogPhase={setDeleteDialogPhaseType}
        />
        <DialogViewDeleteSome
          views={views}
          selectedViewIds={selectedViewIds}
          setSelectedViewIds={setSelectedViewIds}
          open={isDeleteSomeDialogOpen}
          setOpen={setIsDeleteSomeDialogOpen}
        />
      </>
    );
  }, [views, selectedViewIds, deleteDialogPhaseType, isDeleteSomeDialogOpen]);

  const isDisableButton =
    selectedViewIds.length === 0 || !hasVersionsMetaWritePermission;

  const renderDeleteViewsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          if (selectedViewIds.length === 1) {
            setDeleteDialogPhaseType("confirm");
          } else {
            setIsDeleteSomeDialogOpen(true);
          }
        }}
        disabled={isDisableButton}
        className={buttonClasses.warning}
      >
        {t.view.listButton.delete()}
      </Button>
    ),
    [t, selectedViewIds, isDisableButton]
  );
  const renderDuplicateViewsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          setDuplicateRequest((current) =>
            current.phase === "idle"
              ? { phase: "requested", itemIds: selectedViewIds }
              : current
          );
        }}
        disabled={isDisableButton}
      >
        {t.view.listButton.duplicate()}
      </Button>
    ),
    [t, selectedViewIds, isDisableButton]
  );
  return {
    renderDialogsView,
    getContextMenuItemsView,
    selectedViewIds,
    setSelectedViewIds,
    renderDeleteViewsButton,
    renderDuplicateViewsButton,
  };
};
